/**
 * The `reservation.*` capabilities (CAPABILITIES.md §3.4): what a person may do with table bookings.
 * Reservations v2's RV1i (plan §8.1, decisions 7 and 50). The reserved-table rule,
 * `table.orderOnReserved`, is in `table.ts`, because an id's prefix names its file (audit check O).
 *
 * v1 asks for no permission per action inside the Reservations module (inventory-web-module §9) and
 * the backend has no reservation permission either (`vocabulary/backend.ts` holds only
 * `CREATE_ORDERS_ON_RESERVED_TABLE`). What limits a press is therefore not here: the reservation's
 * own rules (`allowedActions`, RV1f) and the partner gate (decision 7) decide it, next to this file.
 * This file answers the one question v1 does ask here: may the entry be seen (licence, online).
 */
import { definePolicies, definePolicy } from "../engine";
import { licence, online } from "../requirements";
import type { NoResource, Requirement } from "../types";

declare module "../types" {
  interface CapabilityMap {
    /** the Reservations entry in the menu and the screen behind it */
    "reservation.view": NoResource;
    /** book a new table reservation */
    "reservation.create": NoResource;
    /** change a saved reservation's details */
    "reservation.edit": NoResource;
    /** give a reservation its table, or move it to another */
    "reservation.assignTable": NoResource;
    /** accept a booking that is waiting for the outlet (a partner or online request) */
    "reservation.confirm": NoResource;
    /** refuse a booking that is waiting for the outlet */
    "reservation.reject": NoResource;
    /** cancel a booking the outlet already holds */
    "reservation.cancel": NoResource;
    /** send the guest the appointment message (SMS, WhatsApp or email) */
    "reservation.sendAppointment": NoResource;
    /** seat the guests: open the order on the booked table */
    "reservation.seat": NoResource;
    /** free the table a booking holds without cancelling the booking */
    "reservation.releaseTable": NoResource;
  }
}

/**
 * The five licences whose menu row shows Reservations: v1's `licences` list on that row
 * (`menuItemsData.ts`, titled "Reservations"). The backend lets any Schedule, Sale or Support
 * module through, so this list is the app's own choice and is narrower than the server's (an `SCH`
 * session is let in by the server and not shown the entry by v1; kept as v1).
 */
export const RESERVATION_LICENCES = ["SEP", "SOL", "SLT", "SLF", "RHP"] as const;

/**
 * What every reservation press needs: the network. The backend has no offline path for
 * reservations (backend.md §7.4) and v1's entry is `isPermittedOffline: false`.
 * One list for all nine press ids, so they cannot drift apart.
 */
const RESERVATION_PRESS_REQUIREMENTS: Requirement[] = [online()];

/**
 * The `reservation.*` policies. `reservation.view` is hidden (not merely disabled) outside the five
 * licences and while offline, as v1's menu filter hides the row (decision 50). Each press shares
 * `RESERVATION_PRESS_REQUIREMENTS` and is `server: "enforced"`, because every reservation route is
 * module-gated by the backend (Schedule, Sale, Support, see backend.md §6.3), so a session without
 * the module is refused there too. Each id is written out, not built in a loop, so a search for it
 * (and audit check O) finds its policy.
 *
 * The menu row's role list (User, Manager, Admin) is deliberately not a requirement: those are every
 * role there is (`ROLES`), so the check selects nothing, and CAPABILITIES.md §3.6 calls a role read
 * in a rule a smell. Its `featureRestrictions` branch does not apply: the row sets no `role` code.
 */
export const RESERVATION_POLICIES = definePolicies("reservation", [
  definePolicy<NoResource>("reservation.view", {
    visibleWhen: [licence(...RESERVATION_LICENCES), online()],
    server: "enforced",
    notes: "The server checks the module (Schedule, Sale or Support), which is wider than v1's five licences.",
  }),
  definePolicy<NoResource>("reservation.create", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.edit", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.assignTable", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.confirm", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.reject", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.cancel", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.sendAppointment", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.seat", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
  definePolicy<NoResource>("reservation.releaseTable", { enabledWhen: RESERVATION_PRESS_REQUIREMENTS, server: "enforced" }),
]);
